import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, EventEmitter, OnChanges, SimpleChanges, Input, Output, HostListener } from '@angular/core';
import { formatDate, formatNumber } from '@angular/common';
import { Observable, concat, filter, from, of, EMPTY, Subscription, Subject } from 'rxjs';
import { map, concatMap, finalize, switchMap, debounceTime, catchError, tap } from 'rxjs/operators';

import { MenuItem, MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { round1, round2 } from 'src/app/shared/round';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { Document, DocItems, DocumentStatusFlag } from 'src/app/shared/document';
import { Product, ProductInfo } from 'src/app/shared/product';
import { Currency } from 'src/app/shared/currency';
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { applyEnrichedData } from 'src/app/shared/doc-items-enricher';

/**
 * Inline-editing table for Purchase Order documents (DOCTYPE 1 / 12).
 *
 * **Pipeline architecture:**
 * - `docItemList$` — debounced `switchMap(getDocItemsByDoc)` that reloads rows when the
 *   document ID changes; triggers `calculateDocItemsFieldsObservable` enrichment.
 * - `calcDocSum$` — debounced (100 ms) Subject that aggregates `DOCSUM*` totals including
 *   `DOCSUMWEIGHT` (unique to PO), then emits a `DocumentStatusFlag` via `getDocumentStatus`.
 *
 * **Row enrichment (`calculateDocItemsFieldsObservable`):** single batch call to
 * {@link DocumentApiService.getDocItemsEnriched}, merged via {@link applyEnrichedData} —
 * replaces the previous four sequential per-row calls (`getDocItemsReceivedByDoc` for
 * shipped qty, `getProductById` for vendor name, `getActcListById` for customs code match,
 * `getProdInfoById` for vendor price/weight/retail price), matching the pattern in
 * `edit-document-content.component.ts`.
 *
 * **Row flags set by `calculateRowSummary`:**
 * - `pdm` — vendor price mismatch: `|U4ETKA - VENDORPRICE| > 0.1`
 * - `wns` — weight not set: `PRODWEIGHT == 0`
 * - `sha` — fully shipped; `mst` — partially shipped
 * - `cnm` / `rnm` / `cpn` / `mtc` — currency/rate/price/customs-code mismatches
 *
 * **Route guard:** `canDeactivate()` + `@HostListener('window:beforeunload')` prevents
 * navigation when `editMode` is active.
 *
 * **Bulk operations:** `updCurrPrice` and `updMainPrice` apply values to all rows.
 *
 * Column visibility is persisted in `localStorage` under key `edit-document-table-columns-2`.
 */
@Component({
  selector: 'app-edit-document-content-po',
  templateUrl: './edit-document-content-po.component.html',
  styleUrls: ['./edit-document-content-po.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentContentPoComponent implements OnInit, OnDestroy, OnChanges {

  private globalSettinsData: GlobalSettingsData = null;
  private docItemQuery: Subscription = null;
  private diSumQuery: Subscription = null;
  private diSaveQuery: Subscription = null;
  private diGroupQuery: Subscription = null;

  /** Debounced Subject that re-aggregates document totals (including weight) and emits `getDocumentStatus`. */
  private calcDocSum$ = new Subject<Document>();
  private calcDocSumSubs: Subscription = null;

  private _productInfoQuery: Subscription = null;
  private _prodShippedQuery: Subscription = null;

  /** Whether to render the table header toolbar. */
  @Input() showHeader: boolean = true;
  /** Whether to show add/edit/delete action controls. */
  @Input() allowEditControls: boolean = true;
  /** Whether to show payment-sum footer row. */
  @Input() showPaysum: boolean = true;

  loading: boolean = false;

  typeDeleteConfirmation: boolean = false;
  confirmText: string = 'DELETE 12';
  deleteAllRows: boolean = false;

  selectedDocType: number = 0;

  sDI: DocItems[] = [];

  allDocItemsChanged: boolean = false;

  docItems: DocItems[] = [];

  docItemsCopy: DocItems[] = [];
  docItemsClone: DocItems[] = [];

  @ViewChild('docItemTable') docItemTable: Table;

  //@HostListener('keydown.space', ['$event'])
  //onSpaceKeyDown(event: KeyboardEvent) {
  //  event.stopPropagation();
  //  event.preventDefault();
  //  if (event.target) {
  //    event.target['value'] = event.target['value'] + ' ';
  //    (event.target as HTMLElement).focus();
  //  }
  //}

  editMode: boolean = false;
  confirmDeleteOn: boolean = false;

  private docItemList$ = new Subject<number>();

  /** The document whose line items are being edited. */
  @Input() inputDocument: Document = null;

  private storeNameShowRowOptions: string = 'edit-document-table-columns-2';

  showDocRowsOptions = [
    { name: 'Инвойс', value: 1 },
    { name: 'УКТЗЕД', value: 4 },
    { name: 'Резерв', value: 8 },
  ];

  showFilterOptions = [
    { name: 'Фильтр', icon: 'pi pi-filter', value: 1, disabled: false },
  ];

  showFilterValue: number[] = [1];

  public get showFilter(): boolean { return this.showFilterValue.includes(1); }

  sU4etka: boolean = false;

  allowOld: boolean = false;

  /** When true, rows with zero `DOCITEMQTY` are included in printed forms. */
  @Input() countEmpty: boolean = false;

  /** Overrides column-visibility bitmask from the caller; skips localStorage restore when set. */
  @Input() setShowRowOptions: number[] = [];

  showRowOptions: number[] = [];

  public get noItems(): boolean { return ((this.docItems || []).length < 1); }

  /*
   *  TODO: load access right to check is U4ETKA is visible to
   */
  public get canSeeInvoice(): boolean { return this.showRowOptions.includes(1); }

  public get seeTariffCode(): boolean { return this.showRowOptions.includes(4); }

  public get seeReservation(): boolean { return this.showRowOptions.includes(8); }

  public get seeDiNote(): boolean { return ([1, 12].includes(this.inputDocument.DOCTYPE)); }

  public get selection(): boolean { return (this.sDI || []).length > 0; }

  public get changes(): boolean { return this.editMode || this.allDocItemsChanged; }

  public get allowTopButtons(): boolean { return this.docItems && (this.docItems.length > 10); }

  public allowSave(di: DocItems): boolean { return di && (di.PRODID ? true : false); }

  cf3: any = {};

  excelExportItems: MenuItem[] = [
    {
      label: 'С логисткой', icon: 'pi pi-star-o', disabled: false, command: () => {
        this.onExportDataWithLogistic();
      }
    }
  ];

  contentReady: boolean = false;

  public get disableExportButton(): boolean { return this.editMode || !this.contentReady || this.noItems; }

  /** Emits `true` when entering edit mode, `false` when edit is saved or cancelled. */
  @Output() editing: EventEmitter<boolean> = new EventEmitter();

  // Initial value indicated serious issue in document items.
  // would clear during the calculation of document profit and emitted to
  // parent component

  documentItemsStatus: number = 101;

  /** Emits a `DocumentStatusFlag` integer after each `calcDocSum$` aggregation cycle. */
  @Output() getDocumentStatus: EventEmitter<number> = new EventEmitter();

  /** Two-way binding companion for `countEmpty`; emits when the user toggles zero-qty inclusion. */
  @Output() countEmptyChange: EventEmitter<boolean> = new EventEmitter();

constructor(
    private prodApi: ProductApiService,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) {

    // Below thread calculate document summary on every change of docItems array
    // in general it is fast and could be called every time when something changed

    // Also this thread check the overall status of document items and emit it to parent component

    this.calcDocSumSubs = this.calcDocSum$.pipe(

      debounceTime(100),
      filter(doc => (doc !== null)),

      concatMap(doc => {

        environment.production || console.log('*** EditDocumentContentComponent calcDocSum$ start: ');

        doc.DOCITEMS = (this.docItems || []).filter(e => this.countEmpty || (e.DOCITEMQTY != 0)).length;
        doc.DOCTOTALITEMS = this.docItems.filter(e => this.countEmpty || (e.DOCITEMQTY != 0)).reduce((r, di) => r + di.DOCITEMQTY, 0);

        doc.DOCSUMU4ETKA = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.U4ETKA, 0);
        doc.DOCVSUM = this.docItems.reduce((r, di) => r + di.DOCITEMQTY * di.DOCITEMVPRICE, 0);
        doc.DOCSUMWOVAT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWOVAT, 0);
        doc.DOCVAT = this.docItems.reduce((r, di) => r + (di.DOCITEMSUM - di.DOCITEMSUMWOVAT), 0);
        doc.DOCSUM = this.docItems.reduce((r, di) => r + di.DOCITEMSUM, 0);
        doc.DOCSUMPROFIT = this.docItems.reduce((r, di) => r + di.DOCITEMPROFITSUM, 0);
        doc.DOCSUMWEIGHT = this.docItems.reduce((r, di) => r + di.DOCITEMSUMWEIGHT, 0);

        this.documentItemsStatus = 0;

        return from(this.docItems).pipe(
          map(di => {

            /* doctypes.includes(1,12)
              рахунок фактура
              комерційна пропозиція
        
                  + (di.bip ? 'Цена ниже входной! ' : '')
                  + (di.bmd ? 'Скидка больше максимальной! ' : '')
                  + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
                  + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
                  + (di.cpn ? 'Валютная цена не соответствует расчетной! ' : '')
                  + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
                  + (di.nta ? 'Не подтвержден УКТЗЕД товара! ' : '')
                  + (di.lpf ? 'Возможный убыток от операции! ' : '')
                  + (di.npc ? 'Не установлена категория товара ' : '')
 
                  di.hlt = (di.bip || di.bmd || di.cnm || di.rnm || di.mtc || di.npc || di.nta || di.lpf || di.cpn);
 
                  di.hlt set to true means that some document item has some issue
 
                  Even single serious issue (bip, lpf) set documentItemsStatus to SeriousError

            if (!di.hlt) {

              if (di.bip || di.cnm || di.rnm || di.mtc || di.lpf) {
                this.documentItemsStatus = DocumentStatusFlag.SeriousError.value;
              }

              if (this.documentItemsStatus < DocumentStatusFlag.SeriousError.value) {

                if (di.cnm || di.rnm || di.mtc || di.lpf) {
                  this.documentItemsStatus = DocumentStatusFlag.MinorIssue.value;
                }
              }
              // Here we need to review all the issue and set final status of document
              // this.documentItemsStatus = 1;
            }
          */

            environment.production || console.log('*** EditDocumentContentComponent in calcDocSum$: ' + JSON.stringify({ DI: di.DOCITEMID, HL: di.hlt }));
          }),

          finalize(() => {

            let status = this.docItems.some(di => di.hlt) ? 1 : 0;
            environment.production || console.log('*** EditDocumentContentComponent in calcDocSum$ finalize: ' + JSON.stringify({ ST: status, DI: this.documentItemsStatus }));
            this.getDocumentStatus.emit(status);
          })
        );
      }))
      .subscribe();

    this.docItemQuery =
      this.docItemList$.pipe(
        debounceTime(300),
        switchMap(docId => {
          this.loading = true;
          console.log('*** EditDocumentContentPoComponent docItemList$ subscribing....: ' + docId)

          return this.docApi.getDocItemsByDoc(docId);

        }),

        tap(data => {
          data.forEach(di => { di.cloneIndex = 0; }); // Reset clone index on reload
        }),

        concatMap(data => this.calculateDocItemsFieldsObservable(data)),   // <= Raw docItems[] from database => fully enriched docItems[]

        tap(() => {
          this.calculateDocumentSummary();        // <= Processed docItems[] with all calculated fields
          // <= Here we have also document summary calculated
          this.loading = false;
        })
      )
        .subscribe(() => console.log('*** EditDocumentContentPoComponent docItemList$ subscription: '));
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  /** Route guard and browser-unload guard — returns `false` (blocks navigation) when `editMode` is active. */
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    return of(!this.editMode);
  }

  reloadDocContent(event) {
    console.log('*** EditDocumentContentPoComponent reloadDocContent: ' + JSON.stringify(this.inputDocument))

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
    }

  }

  saveDiCopy() {

    this.docItemsCopy = [];
    this.docItems.forEach(e => this.docItemsCopy.push({ ...e }));

    //  this.inputDocument.items = [];
    //  this.docItems.forEach(e => this.inputDocument.items.push({ ...e }));
  }

  restoreDiCopy() {
    this.docItems = [];
    this.docItemsCopy.forEach(e => this.docItems.push({ ...e }));

    //    this.inputDocument.items = [];
    //    this.docItems.forEach(e => this.inputDocument.items.push({ ...e }));
  }

  calculateFastRowSummary(di: DocItems) {
    //    console.log('*** EditDocumentContentPoComponent calculateFastRowSummary: ' + di.DOCITEMID);

    di.DOCITEMSUMWOVAT = di.DOCITEMQTY * di.DOCITEMPRICEWOVAT;
    di.DOCITEMSUM = di.DOCITEMQTY * di.DOCITEMPRICE;
    di.DOCITEMVSUM = di.DOCITEMQTY * di.DOCITEMVPRICE;
    di.DOCITEMSUMU4 = di.DOCITEMQTY * di.U4ETKA;
  }

  /**
   * Computes per-row weight, retail price, shipping dates, and warning flags.
   * Sets `pdm` (vendor price mismatch) and `wns` (weight not set) unique to PO.
   */
  calculateRowSummary(di: DocItems) {
//    console.log('*** EditDocumentContentPoComponent calculateRowSummary: ' + di.DOCITEMID);

      this.calculateFastRowSummary(di);

    di.DOCITEMSUMWEIGHT = di.prodInfo ? (di.DOCITEMQTY * (di.prodInfo.PRODWEIGHT ?? 0)) : 0;

    di.DOCITEMRETPRICE = di.prodInfo.PRODPRICE * this.inputDocument.DOCRATEVAL;
    di.DOCITEMRETVPRICE = di.prodInfo.PRODPRICE;

    try {
      di.DOCITEMPOSHIPPINGDATE = new Date(di.DOCITEMTEXT1);
    } catch (err) {
      di.DOCITEMPOSHIPPINGDATE = null;
    }

    di.sha = this.inputDocument && (di.DOCITEMQTY == di.DOCITEMSHIPPED);
    di.mst = this.inputDocument && (di.DOCITEMQTY > di.DOCITEMSHIPPED);

    di.cnm = this.inputDocument && (di.DOCITEMCURRID != this.inputDocument.DOCCURRID);
    di.rnm = this.inputDocument && (di.DOCITEMCRATE != this.inputDocument.DOCRATEVAL);
    di.cpn = round1(di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) != round1(di.DOCITEMPRICE);

    di.mtc = ((di.PRODCUSTCODE || '') == '');

    di.pdm = Math.abs(di.U4ETKA - di.prodInfo.VENDORPRICE) > 0.1;
    di.wns = ((di.prodInfo.PRODWEIGHT ?? 0) == 0);

    di.hlt = (di.cnm || di.rnm || di.mtc || di.pdm || di.wns || di.cpn);

    this.calculateDocumentSummary();
  }

  /**
   * Enriches a raw `DocItems[]` array returned by the API with shipped quantity, vendor
   * name, customs code validation, and product info (vendor price, weight, retail prices).
   *
   * Single batch call to {@link DocumentApiService.getDocItemsEnriched} (replaces the four
   * sequential per-row calls the previous `calculateDocItemsFields` made — see the commented
   * implementation below), merged via {@link applyEnrichedData}. `calculateRowSummary` then
   * runs synchronously per row (no further HTTP), same as before.
   *
   * @param data - Raw `DocItems[]` freshly loaded from the database.
   * @returns An observable that emits the fully processed `DocItems[]` once.
   */
  calculateDocItemsFieldsObservable(data: DocItems[]): Observable<DocItems[]> {
    console.log('*** EditDocumentContentPoComponent calculateDocItemsFieldsObservable')

    this.contentReady = false;

    return of(data).pipe(

      // Single batch call — replaces 4 x N per-row HTTP requests
      switchMap(rawItems => this.docApi.getDocItemsEnriched(
        this.inputDocument.DOCID,
        this.inputDocument.DOCMANCLIID,
        this.inputDocument.DOCCURRID,
        //
        // It depends on document type ????
        //
        this.globalSettinsData.mainStockId,
        this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
        this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
        //
        // Is it on actual date or document date
        // Could depends on....
        // TODO: We have to divide the price and reservation info
        // One need on actual date, but some on document date....
        // Complicated....
        //
        // new Date(this.inputDocument.DOCDATE)
        new Date()
      ).pipe(
        catchError(this.handleHttpError('getDocItemsEnriched', [])),
        map(enriched => applyEnrichedData(rawItems, enriched, this.globalSettinsData.vatValue, this.inputDocument.DOCRATEVAL))
      )),

      // Row flags / weight / retail price (synchronous math, no HTTP)
      map(items => {
        items.forEach(di => this.calculateRowSummary(di));
        return items;
      }),

      tap(items => { this.docItems = [...items]; }),

      finalize(() => {
        this.inputDocument.items = this.docItems;
        this.saveDiCopy();
        this.contentReady = true;
      })
    );
  }

  // Superseded by calculateDocItemsFieldsObservable (batch getDocItemsEnriched + applyEnrichedData) — kept for reference.
  //
  // /**
  //  * Enriches raw `DocItems[]` with shipped quantity, vendor name, customs code validation,
  //  * and product info (vendor price, weight, retail prices) via sequential `concatMap` calls.
  //  */
  // calculateDocItemsFields(data: DocItems[]) {
  //   console.log('*** EditDocumentContentPoComponent calculateDocItemsFields')
  //   this.docItems = [...data];
  //
  //   this.contentReady = false;
  //
  //   this.diSumQuery =
  //     from(this.docItems).pipe(
  //       concatMap(di => {
  //
  //         di.cloneIndex = 0;
  //         di.PRODVAT = (di.PRODVAT ?? this.globalSettinsData.vatValue);
  //         di.DOCITEMSHIPPED = 0;
  //
  //         this.calculateFastRowSummary(di);
  //
  //         return of(di);
  //       }),
  //
  //       concatMap(di => this.docApi.getDocItemsReceivedByDoc(di.DOCITEMID, new Date()).pipe(
  //         map(data => { di.DOCITEMSHIPPED = data[0].DOCITEMSHIPPED; return di; }),
  //         catchError(this.handleHttpError('getDocItemsReceivedByDoc', di))
  //       )),
  //
  //       concatMap(di => this.prodApi.getProductById(di.PRODID).pipe(
  //         map(pi => { di.PRODOEMNAME = (pi.length > 0) ? pi[0].PRODVENDORNAME : ''; return di; }),
  //         catchError(this.handleHttpError('getProductById', di))
  //       )),
  //
  //       concatMap(di => this.prodApi.getActcListById(di.PRODID).pipe(
  //         map(actc => {
  //           di.nta = true;
  //           if ((actc || []).length > 0) {
  //             di.nta = !(di.PRODCUSTCODE == actc[0].ACTC_STRING);
  //           }
  //           return di;
  //         }),
  //         catchError(this.handleHttpError('getActcListById', di))
  //       )),
  //
  //       concatMap(di => this.prodApi.getProdInfoById(
  //         di.PRODID,
  //         this.inputDocument.DOCMANCLIID,
  //         this.inputDocument.DOCCURRID,
  //         this.globalSettinsData.mainStockId,
  //         this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
  //         this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
  //         new Date()
  //       )
  //         .pipe(
  //           map(data => {
  //             di.prodInfo = data[0];
  //             this.calculateRowSummary(di);
  //           }),
  //           catchError(this.handleHttpError('getProdInfoById'))
  //         )
  //       ),
  //
  //       finalize(() => {
  //         this.contentReady = true;
  //       })
  //     )
  //       .subscribe();
  //
  //   this.inputDocument.items = this.docItems;
  //
  //   this.saveDiCopy();
  // }

  calculateDocumentSummary() {
    //console.log('*** EditDocumentContentPoComponent calculateDocumentSummary: ')

    this.calcDocSum$.next(this.inputDocument);
  }

  private handleHttpError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed:`, error);
      const detail = error?.status === 0 ? `Немає з'єднання з сервером`
        : error?.status >= 500 ? 'Помилка сервера'
        : error?.message || 'Невідома помилка';
      this.messageService.add({ severity: 'error', summary: 'Помилка завантаження', detail, life: 5000 });
      return result !== undefined ? of(result) : (EMPTY as Observable<T>);
    };
  }

  /** Persists column visibility to localStorage and unsubscribes all active subscriptions. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentContentPoComponent ngOnDestroy: ')

    if (!this.setShowRowOptions) {
      localStorage.setItem(this.storeNameShowRowOptions, JSON.stringify(this.showRowOptions));
    }
    localStorage.setItem(this.storeNameShowRowOptions + '-columns-filter-state', JSON.stringify(this.showFilterValue));

    if (this.docItemQuery) { this.docItemQuery.unsubscribe(); }
    if (this.calcDocSumSubs) { this.calcDocSumSubs.unsubscribe(); }
    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }
    if (this._prodShippedQuery) { this._prodShippedQuery.unsubscribe(); }
    if (this.diSaveQuery) { this.diSaveQuery.unsubscribe(); }
    if (this.diSumQuery) { this.diSumQuery.unsubscribe(); }
    if (this.diGroupQuery) { this.diGroupQuery.unsubscribe(); }
  }

  /**
   * Reacts to `inputDocument` and `setShowRowOptions` changes.
   * - `inputDocument`: pushes the document ID onto `docItemList$` to reload rows.
   * - `setShowRowOptions`: overrides the column visibility bitmask immediately.
   */
  ngOnChanges(changes: SimpleChanges): void {
    //    console.log('*** EditDocumentContentPoComponent ngOnDestroy: ')
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentContentPoComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //      console.log('*** EditDocumentContentPoComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'setShowRowOptions':
          this.showRowOptions = [...change.currentValue];
          break;

        case 'inputDocument':
          if (change.currentValue) {
            this.docItemList$.next(change.currentValue.DOCID);
          }
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, restores column state from localStorage, and wires up `docItemList$` and `calcDocSum$` pipelines. */
  ngOnInit(): void {
    console.log('*** EditDocumentContentPoComponent ngOnInit: ')


    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    if (!this.setShowRowOptions) {
      let _storedShowRowOptions: string = localStorage.getItem(this.storeNameShowRowOptions);
      if (_storedShowRowOptions) { this.showRowOptions = JSON.parse(_storedShowRowOptions); }
    }
    let _storedFilterOptions: string = localStorage.getItem(this.storeNameShowRowOptions + '-columns-filter-state');
    if (_storedFilterOptions) { this.showFilterValue = JSON.parse(_storedFilterOptions); }

    this.reloadDocContent(null);
  }

  onRowsOptionChange(event) {
    //  console.log('*** EditDocumentContentPoComponent in doChangeConditions: ' + JSON.stringify(event));
    //  console.log('*** EditDocumentContentPoComponent in doChangeConditions (showRowOptions): ' + JSON.stringify(this.showRowOptions));
  }

  onAddRow(event) {
    console.log('*** EditDocumentContentPoComponent in onAddRow: ' + JSON.stringify(event));

    let maxRowIndex = 0;

    this.docItems.forEach(pp => { maxRowIndex = Math.max(maxRowIndex, pp.PAYITEMCLIID) });

    this.docItems = [...this.docItems, {

      DOCITEMID: -2,
      DOCID: this.inputDocument.DOCID,
      DOCTYPE: this.inputDocument.DOCTYPE,
      PRODID: null,
      DOCITEMPRODNAME: '',
      DOCITEMPRICE: 0,
      DOCITEMQTY: 1,
      DOCITEMVPRICE: 0,
      DOCITEMCURRID: this.inputDocument.DOCCURRID,
      DOCITEMCRATE: this.inputDocument.DOCRATEVAL,
      PAYITEMCLIID: maxRowIndex + 10,
      PAYITEMDEPID: null,
      DOCITEMTEXT1: '',
      DOCITEMTEXT2: 'Нет',
      CURCODE: '',
      U4ETKA: null,
      PRODVAT: this.globalSettinsData.vatValue,
      cloneIndex: 0
    }];

    let ppe: DocItems = this.docItems.find(e => e.DOCITEMID == -2);
    this.onDiRowEditInit(ppe);
    this.docItemTable.initRowEdit(ppe);
  }

  onDiRowEditInit(esg: DocItems) {
    console.log('*** EditDocumentContentPoComponent in onPpRowEditInit: entry');
    //environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.docItems.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditDocumentContentPoComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.docItemsClone.push({ ...esg });

    esg.DOCITEMCURRID = this.inputDocument.DOCCURRID;
    esg.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;

    this.editMode = true;
    this.editing.emit(this.editMode);
}

  onDiRowEditCancel(esg: DocItems, index: number) {
    console.log('*** EditDocumentContentPoComponent in onPpRowEditCancel: ' + index);
    //environment.production || console.table(esg);

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);
    esg.DOCITEMVPRICE = esg.DOCITEMPRICE / esg.DOCITEMCRATE;

    if (esg.DOCITEMID == -2) {
      // Cancel the newly created record
      // simply delete

      this.docItems.splice(index, 1);

    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.docItems[index] = { ...this.docItemsClone[ppid] };
      this.docItems[index].cloneIndex = 0;
    }

    this.docItemsClone.splice(ppid, 1);

    this.editMode = false;
    this.editing.emit(this.editMode);
}

  onDiRowEditSave(esg: DocItems) {
    console.log('*** EditDocumentContentPoComponent in onDiRowEditSave: ');

    let ppid = this.docItemsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    this.diSaveQuery =
      concat(((esg.DOCITEMID == -2) ? this.docApi.postDocItemById(esg) : this.docApi.putDocItemById(esg))
        .pipe(map((count) => {
          console.log('*** EditDocumentContentPoComponent in onSubscribe to Save: ' + count);

          this.docItemsClone.splice(ppid, 1);
          esg.cloneIndex = 0;
          this.reloadDocContent(null);
          this.editMode = false;
          this.editing.emit(this.editMode);
        })),

      )
        .subscribe();
  }

  onProductSelection(event: Product, di: DocItems) {
    console.log('*** EditDocumentContentPoComponent onProductSelection: ' + JSON.stringify(event));
    di.PRODID = event.PRODID;
    di.PRODCODE = event.PRODCODE;
    di.PRODVAT = event.PRODVAT;

    this._productInfoQuery =
      this.prodApi.getProdInfoById(di.PRODID, this.inputDocument.DOCMANCLIID, this.inputDocument.DOCCURRID, this.inputDocument.DOCMANDEPID, 0, 0, new Date()).pipe(map(data => {
        di.prodInfo = data[0];

        di.DOCITEMPRODNAME = event.PRODNAME;
        di.U4ETKA = di.prodInfo.VENDORPRICE;
        di.DOCITEMVPRICE = di.prodInfo.PRODINPRICE;
        di.DOCITEMPRICE = di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL;

      }))
        .subscribe();
  }

  onDOCITEMQTYChange(event, di: DocItems) {

    this.calculateRowSummary(di);
  }

  onDOCITEMVPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPoComponent in onDOCITEMVPRICEChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentPoComponent in onDOCITEMQTYChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round1((di.DOCITEMVPRICE * this.inputDocument.DOCRATEVAL) / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMPRICE = round2(di.DOCITEMPRICEWOVAT * ((100.0 + di.PRODVAT) / 100.0));

    this.calculateRowSummary(di);
  }

  onDOCITEMPRICEChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPoComponent in onDOCITEMPRICEChange: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPoComponent in onDOCITEMPRICEChange: ' + JSON.stringify(di));

    di.DOCITEMPRICEWOVAT = round1(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
    di.DOCITEMVPRICE = round2(di.DOCITEMPRICE / this.inputDocument.DOCRATEVAL);

    this.calculateRowSummary(di);
  }

  onDOCITEMU4ETKAChange(event, di: DocItems) {
    //console.log('*** EditDocumentContentPoComponent in onDOCITEMPRICEChange: ' + JSON.stringify(event));
    //console.log('*** EditDocumentContentPoComponent in onDOCITEMPRICEChange: ' + JSON.stringify(di));

    this.calculateRowSummary(di);
  }

  onDateSelect(event, di: DocItems) {
    console.log('*** EditDocumentContentPoComponent in onDateSelect: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPoComponent in onDateSelect: ' + JSON.stringify(di));
    di.DOCITEMTEXT1 = di.DOCITEMPOSHIPPINGDATE.toISOString();
    this.calculateRowSummary(di);
  }

  checkDiState(event, di: DocItems) {
    console.log('*** EditDocumentContentPoComponent in checkDiState: ' + JSON.stringify(event));
    console.log('*** EditDocumentContentPoComponent in checkDiState: ' + JSON.stringify(di));
    if (di.hlt) {
      this.messageService.add({
        severity: 'warn',
        summary: di.PRODCODE + ' информация: ',
        life: 10000,
        detail: ''
          + (di.cnm ? 'Валюта не соответствует установленной в документе! ' : '')
          + (di.rnm ? 'Курс не соответствует установленному в документе! ' : '')
          + (di.cpn ? 'Валютная цена не соответствует расчетной! ' : '')
          + (di.mtc ? 'Не указан УКТЗЕД товара! ' : '')
          + (di.wns ? 'Не указан вес товара! ' : '')
          + (di.pdm ? 'Не совпадает цена в инвойсе и в базе! ' : '')
      });

    } else {
      this.messageService.add({ severity: 'success', summary: di.PRODCODE + ' информация: OK' });
    }
  }

  onRowSelect(event) {
    console.log('*** EditDocumentContentPoComponent in onRowSelect: ' + JSON.stringify(event));
  }

  /** Bulk-updates all rows currency and rate fields to match the current document currency. */
  updCurrPrice(event) {
    console.log('*** EditDocumentContentPoComponent in updCurrPrice: ' + JSON.stringify(event));
    this.diGroupQuery = from(this.docItems)
      .pipe(
        map(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMVPRICE = e.DOCITEMPRICE / e.DOCITEMCRATE;
          this.calculateRowSummary(e);
        }),
        finalize(() => {
          this.allDocItemsChanged = true;
          this.editing.emit(this.allDocItemsChanged);
        }))
      .subscribe();
  }

  /** Bulk-updates all rows' UAH retail price (`DOCITEMPRICE`) from `DOCITEMVPRICE * DOCITEMCRATE`. */
  updMainPrice(event) {
    console.log('*** EditDocumentContentPoComponent in updMainPrice: ' + JSON.stringify(event));
    this.diGroupQuery = from(this.docItems)
      .pipe(
        map(e => {
          e.DOCITEMCURRID = this.inputDocument.DOCCURRID;
          e.DOCITEMCRATE = this.inputDocument.DOCRATEVAL;
          e.DOCITEMPRICE = round2(e.DOCITEMVPRICE * e.DOCITEMCRATE);
          this.calculateRowSummary(e);
        }),
        finalize(() => {
          this.allDocItemsChanged = true;
          this.editing.emit(this.allDocItemsChanged);
        }))
      .subscribe();
  }

  /** Persists all unsaved rows to the API sequentially via `putDocItemById`, then re-enriches. */
  onSaveRows(event) {
    console.log('*** EditDocumentContentPoComponent in onSaveRows: ' + JSON.stringify(event));

    this.loading = true;

    this.diSaveQuery = from(this.docItems)
      .pipe(
        concatMap(e => this.docApi.putDocItemById(e)),
        finalize(() => {
          this.reloadDocContent(null);
          this.calculateDocItemsFieldsObservable(this.docItems).subscribe();
          this.allDocItemsChanged = false;
          this.editing.emit(this.allDocItemsChanged);
          this.loading = false;
        }))
      .subscribe();

    //this.diGroupQuery
    //  = from(this.docItems)
    //    .pipe(switchMap(di => of(di)))
    //    .subscribe(di => {
    //    });

    //let count = this.docItems.length;

    //this.docItems.forEach(e => {
    //  this.diSaveQuery = this.docApi.putDocItemById(e).pipe(map(() => { this.reloadDocContent(null); })).subscribe(() => {
    //    console.log('*** EditDocumentContentPoComponent in onSaveRows: count: ' + count);
    //    if (--count == 0) {
    //      this.calculateDocItemsFields(this.docItems);
    //      this.allDocItemsChanged = false;
    //      this.editing.emit(this.allDocItemsChanged);
    //    }
    //  });
    //});
  }

  /** Reverts all rows to the last saved copy and re-enriches, clearing the `allDocItemsChanged` flag. */
  onUndoRows(event) {
    console.log('*** EditDocumentContentPoComponent in onUndoRows: ' + JSON.stringify(event));
    console.table(this.docItems);
    console.table(this.docItemsCopy);

    this.restoreDiCopy();
    this.calculateDocItemsFieldsObservable(this.docItems).subscribe();
    this.allDocItemsChanged = false;
    this.editing.emit(this.allDocItemsChanged);
}

  onRemoveSelection(event) {
    console.log('*** EditDocumentContentPoComponent in onRemoveSelection: ' + JSON.stringify(event));
    this.sDI = [];
  }

  onRemoveAllRow(event) {
    console.log('*** EditDocumentContentPoComponent in onRemoveRow: ' + JSON.stringify(event));

    this.deleteAllRows = true;
    this.confirmText = 'DELETE ' + this.docItems.length;
    this.typeDeleteConfirmation = true;
    this.confirmDeleteOn = true;
  }

  onRemoveRow(event) {
    console.log('*** EditDocumentContentPoComponent in onRemoveRow: ' + JSON.stringify(event));
    this.deleteAllRows = false;
    this.typeDeleteConfirmation = false;
    this.confirmDeleteOn = true;
  }

  onDeleteConfirmation(event) {
    console.log('*** EditDocumentContentPoComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditDocumentContentPoComponent onDeleteConfirmation DO DELETE!!! NOW!!!');

      if (this.deleteAllRows) {
        console.log('*** EditDocumentContentPoComponent onDeleteConfirmation DO DELETE ALL !!! NOW!!! ********** ALL ********');
        this.docApi.deleteAllDocItemByDocId(this.inputDocument.DOCID)
          .subscribe(() => {
            console.log('*** EditDocumentContentPoComponent onDeleteAllItemsConfirmation: DONE!');
            this.sDI = [];
            this.reloadDocContent(null);
          });
      } else {

        concat(
          from(this.sDI).pipe(concatMap(row => this.docApi.deleteDocItemById(row.DOCITEMID))),
          of(1).pipe(map( val => {
            this.sDI = [];
            this.reloadDocContent(null);
          }))
        )
          .subscribe();
      }
    }
  }

  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /*
  DOCID  DOCTYPEID  DOCDATE  DOCEXPDATE  DOCVALDATE  DOCCLIENTSID  DOCPERSONID
  DOCMANCLIID  DOCMANPERID  DOCNUMBER  DOCNOTE1  DOCNOTE2  PRIMDOCID  DOCCLIDEPID
  DOCMANDEPID  DOCCURRID  DOCRATEVAL  DOCACTIVE  DOCFLAG  CLIENTSNAME  DOCLCOCLIID
  LOCALCOMP  PERSONNAME  DOCNAME  DOCSUM  PAYDSUM  SHIPSUM  AUTHOR  CURSTR  LOCALCOMPID
  PAIDJUSTNOW  PAIDNOWDATE  DOCPCOCLIID  PARTNERNAME  PARTNERNAMESTR
  fs	ps	fp	pp	np	go	td	fd
  */

  onExportData() {
    console.log('*** EditDocumentContentPoComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY != 0))
          .map(e => ({

          'Код товара': e.PRODCODE,
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Получено': e.DOCITEMSHIPPED,
          'Таможенный код': e.PRODCUSTCODE,
          'Дата отгрузки': new Date(e.DOCITEMTEXT1).toLocaleDateString("lt-LT"),
          'Текст 2': e.DOCITEMTEXT2,
          'Цена в базе': e.prodInfo.VENDORPRICE,
          'Цена по инвойсу': e.U4ETKA,
          'Сумма по инвойсу': e.DOCITEMSUMU4,

          'Продано': e.prodInfo.PRODSOLD,
          'Сумма проданного': e.prodInfo.PRODSOLD * e.U4ETKA,

          'Вес': e.prodInfo.PRODWEIGHT,
          'Общий вес': e.DOCITEMSUMWEIGHT,

          'Цена с НДС': e.DOCITEMPRICE,
          'Сумма с НДС': e.DOCITEMSUM,
          'Цена в валюте': e.DOCITEMVPRICE,
          'Сумма в валюте': e.DOCITEMVSUM,
          'Ставка НДС': e.PRODVAT,

          'Валюта': e.CURCODE,
          'Курс': e.DOCITEMCRATE,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  checkCommand(event) {
    this.excelExportItems.forEach(e => e.disabled = this.noItems);
  }

  onExportDataWithLogistic() {
    console.log('*** EditDocumentContentComponent onExportData: ')

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.docItems);

      let exportData: any =
        this.docItems
          .filter(e => this.countEmpty || (e.DOCITEMQTY != 0))
          .map(e => ({

          'Код товара': e.PRODCODE,
          'Производитель': e.PRODOEMNAME,
          'Поставщик': e.prodInfo?.VENDORNAME,   // default supplier (ProdVendors), not the manufacturer
          'Наименование товара': e.DOCITEMPRODNAME,
          'Количество': e.DOCITEMQTY,
          'Цена с НДС': e.DOCITEMPRICE,
          'Сумма с НДС': e.DOCITEMSUM,
          'Цена в валюте': e.DOCITEMVPRICE,
          'Сумма в валюте': e.DOCITEMVSUM,
          //'Цена без НДС': e.DOCITEMPRICEWOVAT,
          //'Сумма без НДС': e.DOCITEMSUMWOVAT,
          'Ставка НДС': e.PRODVAT,
          'Таможенный код': e.PRODCUSTCODE,
          'Текст 1': e.DOCITEMTEXT1,
          'Текст 2': e.DOCITEMTEXT2,

          'Получено': e.DOCITEMSHIPPED,

          'На складе': e.prodInfo.PRODSTOCK,
          'В резерве': e.prodInfo.PRODRESERVE,
          'Продано': e.prodInfo.PRODSOLD,
          'В пути': e.prodInfo.PRODONWAY,

          'Валюта': e.CURCODE,
          'Курс': e.DOCITEMCRATE,

          'Розница': e.DOCITEMRETPRICE,
          'Розница в валюте': e.DOCITEMRETVPRICE,
          //'Скидка': e.DISCOUNT,

          //'Входная цена': e.DOCINCOMEPRICE,
          //'Доход': e.DOCITEMPROFIT,
          //'Сумма дохода': e.DOCITEMPROFITSUM,

          'Инвойс': e.U4ETKA,
          'Сумма инвойса': e.DOCITEMSUMU4,

          'Цена поставщика': e.prodInfo.VENDORPRICE,
          'Вес': e.prodInfo.PRODWEIGHT,
          'Доставка': e.prodInfo.VENADDCONST,
          'Пошлина': e.prodInfo.VENMULCONST,
          'Дополнительно +': e.prodInfo.VENADDVAR,
          'Дополнительно *': e.prodInfo.VENMULVAR,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { document: worksheet }, SheetNames: ['document'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "document");
    });
  }

  onStateSave(state) {
    console.log('*** EditDocumentContentPoComponent onStateSave: ' + JSON.stringify(state));
  }

  onStateRestore(state) {
    console.log('*** EditDocumentContentPoComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        let fltItem = state.filters[f];
        this.cf3[f] = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;
      }
    }
  }

  cleanSortMode(state) {
    console.log('*** EditDocumentContentComponent cleanSortMode: ' + JSON.stringify(state));

    this.docItemTable.reset();
    this.reloadDocContent(state);
  }

  updateRowIndexes(state) {
    console.log('*** EditDocumentContentComponent updateRowIndexes: ' + JSON.stringify(state));

    let rowIndex = 10;
    this.loading = true;

    this.diSaveQuery =
      concat(
        from(this.docItems)
          .pipe(concatMap(di => {
            di.PAYITEMCLIID = rowIndex;
            rowIndex += 10;

            return this.docApi.putDocItemById(di);
          })),

        of(false).pipe(map(data => this.loading = data))
      )
        .subscribe();
  }

  countEmptyModeChange(event) {
    console.log('*** EditDocumentContentComponent countEmptyModeChange: ' + JSON.stringify(event));

    this.countEmpty = event.checked;

    if (this.inputDocument) {
      this.docItemList$.next(this.inputDocument.DOCID);
      this.countEmptyChange.emit(this.countEmpty);
    }
  }
}

